import { SELECTED_PROTOCOLS_KEY } from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import type { Vault } from '@/types/vault'
import { getProtocolDisplayName } from '@/utils/protocol'
import type { PopoverProps } from '@radix-ui/react-popover'
import { getProtocolImage } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { AccordionItem } from '../common/accordion'
import { RoundImage } from '../common/roundImage'
import { FilterAccordionContent, FilterAccordionTrigger, FilterSelect } from './filterSelect'
import { FilterListContent } from './filterSelectContent'

interface Props {
  vaults: Vault[]
  className?: string
}

function useProtocolFilterData(vaults: Vault[]) {
  const { selectedProtocols, updateSearchState } = useVaultsSearchState()
  const protocols = useMemo(
    () => [...new Set(vaults.map((vault) => vault.protocol.name.toLowerCase()))].sort(),
    [vaults]
  )
  const protocolDisplayNames = useMemo(
    () =>
      Object.fromEntries(
        vaults.map((vault) => [vault.protocol.name.toLowerCase(), getProtocolDisplayName(vault.protocol)])
      ),
    [vaults]
  )
  const { trackEvent } = useMixpanel()

  return {
    items: protocols,
    selectedItems: selectedProtocols,
    setSelectedItems: (value: string[]) => {
      updateSearchState([SELECTED_PROTOCOLS_KEY, value])
      trackEvent('Filter used', { Filter: 'protocol', Value: value.join(', ') })
    },
    name: 'protocol',
    renderItemPrefix: (item: string) => (
      <RoundImage
        src={getProtocolImage(item)}
        fallback={getProtocolImage('fallback')}
        alt={`${protocolDisplayNames[item] ?? item} protocol`}
        className="size-7"
      />
    ),
    itemFormatter: (item: string) => protocolDisplayNames[item] ?? item,
    searchNormalizer: (item: string) => `${item} ${protocolDisplayNames[item]?.toLowerCase() ?? ''}`,
  }
}

export function ProtocolFilter({ vaults, className, modal }: Props & Pick<PopoverProps, 'modal'>) {
  const props = useProtocolFilterData(vaults)
  return <FilterSelect {...props} scrollClassName="max-h-56" modal={modal} className={className} />
}

export function ProtocolFilterAccordion({ vaults }: Props) {
  const props = useProtocolFilterData(vaults)
  return (
    <AccordionItem value="protocol">
      <FilterAccordionTrigger name="protocol" />
      <FilterAccordionContent>
        <FilterListContent {...props} scrollClassName="max-h-48" />
      </FilterAccordionContent>
    </AccordionItem>
  )
}
